import fetch from "@lib/fetch";
import axios from "axios";
import { API } from "../../config";
import { isAuthenticated } from "./auth";
import moment from "moment";
import { firstCategoryOptions as categories, getTypeFilter, secondCategoryOptions as options } from "screens/tickets/helpers/constant";
import imageCompression from "browser-image-compression";
import { TICKET } from "./_ActionTypes";
import { authorizationHeader } from "@lib/token";

const failure = { success: false }

function adaptTypeString(categoryString) {
  if (categories.some((option) => option.value === categoryString))
    return options(categoryString).map((option) => option.value);
  return categoryString;
}

function adaptTicketJSON(ticket) {
  const { user } = isAuthenticated();

  const [couriers, orderIds] = ticket.resources.reduce(([couriers, orderIds], resource) => {

    if (resource.document.type !== "shipping.order") {
      return [couriers, orderIds];
    }

    couriers.push(resource.document.metadata.courier_code);
    orderIds.push(resource.document.metadata.tracking_number);

    return [couriers, orderIds];
  }, [[], []])

  return {
    id: ticket.id,
    order_ids: orderIds,
    title: ticket.title,
    description: ticket.content,
    reference_id: ticket.ref,
    reporter_contact_name: ticket.reporter?.name,
    reporter_contact_phone: ticket.reporter?.phone,
    support_image_links: ticket.attachments.map((attachment) => attachment.url),
    couriers: couriers,
    is_read_by_user: !ticket.updated,
    type: ticket.category,
    channel: "biteship",
    status: adaptTicketStatusJSON(ticket.status),
    created_by: adaptTicketCreatorJSON(ticket),
    comments: ticket.comments.map((comment) => ({
      content: comment.content,
      self: user._id === comment.author.id,
      user_first_name: comment.author.name.split(" ")[0],
      user_last_name: comment.author.name.split(" ").slice(1).join(" "),
      user_role: adaptRoleJSON(comment.author.role),
      updated_at: comment.updated_at,
    })),
    created_at: ticket.created_at,
    solved_at: ticket.resolved_at,
  }
}

function adaptRoleJSON(roleJSON) {
  switch (roleJSON) {
    case "admin":
      return "admin";
    case "customer":
    default:
      return "owner";
  }
}

const TICKET_STATUS_JSON_TUPLES = [
  ["open", "active"],
  ["resolved", "solved"],
  ["in_progress", "processing"],
  ["closed", "deleted"],
];

function adaptTicketStatusJSON(statusJSON) {
  const tuple = TICKET_STATUS_JSON_TUPLES.find((tuple) => tuple[0] === statusJSON)
  if (!tuple) return null;
  return tuple[1]
}

function adaptTicketStatusString(statusString) {
  const tuple = TICKET_STATUS_JSON_TUPLES.find((tuple) => tuple[1] === statusString)
  if (!tuple) return null;
  return tuple[0]
}

function adaptTicketCreatorJSON(ticket) {
  switch (ticket.owner.role) {
    case "admin":
      return "admin";
    default:
      return "self";
  }
}

function ticketFilter(url, filter) {
  if (typeof filter.unread === "boolean")
    url.searchParams.append("updated_is", filter.unread.toString());

  if (filter.status?.length)
    filter.status.forEach((status) => url.searchParams.append("status_in", adaptTicketStatusString(status)))

  if (filter.type?.length)
    filter.type.forEach((typeString) => {
      const values = adaptTypeString(typeString);

      if (Array.isArray(values)) {
        values.forEach((value) => url.searchParams.append("category_in", value))
        return;
      };

      url.searchParams.append("category_in", values);
    });

  if (filter.createdStartDate)
    url.searchParams.append("created_at_gte", moment(filter.createdStartDate).toISOString());

  if (filter.createdEndDate)
    url.searchParams.append(
      "created_at_lt",
      moment(filter.createdEndDate).add(1, "days").toISOString()
    );

  if (filter.search) {
    url.searchParams.append("resources.document_type_is", "shipping.order");
    url.searchParams.append("or[0][resources.document_metadata][tracking_number_is]", filter.search);
    url.searchParams.append("or[1][resources.document_id_is]", filter.search);
  };

  if (filter.orderId)
    url.searchParams.append("resources.document_id_is", filter.orderId);

  return url;
}

export const createTicket = async (data) => {
  const payload = {
    type: data.type,
    title: data.title,
    description: data.description,
    reporter_contact_name: "",
    reporter_contact_phone: "",
    channel: "biteship",
    order_ids: data.orderIds,
    reference_id: "",
    is_notify_to_email: false,
    is_notify_to_phone: false,
    ticket_from_order: false,
    created_by: "self",
    support_image_links: [],
  };

  if (data.images.length) {
    const imagesLinks = await Promise.all(
      data.images.map(async (file) => {
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
        });
        const resUploadImage = await uploadContentBlob(compressedFile);
        if (resUploadImage?.success) return resUploadImage.image;
        return null;
      })
    );

    payload.support_image_links = imagesLinks.filter((link) => link);
  }

  return fetch(`${API}/v1/tickets`, {
    mode: "cors",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
    body: JSON.stringify(payload),
  })
    .then((res) => {
      return res.json();
    })
    .catch((error) => {
      console.log(error);
    });
};

export async function getTickets(filter) {
  try {
    const baseURL = new URL("/v1/tickets", API)
    const ticketsURL = ticketFilter(baseURL, filter);

    const response = await fetch(ticketsURL.toString(), {
      method: "GET",
      headers: {
        "X-Use-Legacy-ID": "true",
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: authorizationHeader(),
      },
    });

    if (!response.ok) {
      return failure;
    }

    const responseJSON = await response.json();
    const result = {
      success: true,
      tickets: responseJSON.tickets.map((ticket) => adaptTicketJSON(ticket)),
    }

    return result;
  }

  catch (error) {
    console.error(error);
    return failure;
  }
};

export async function getTicket(ticketId) {
  try {
    const response = await fetch(`${API}/v1/tickets/${ticketId}`, {
      method: "GET",
      headers: {
        "X-Use-Legacy-ID": "true",
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${isAuthenticated().token}`,
      },
    })

    if (!response.ok) {
      return { success: false };
    }

    const responseJSON = await response.json();

    return {
      success: true,
      ...adaptTicketJSON(responseJSON.ticket),
    };
  }

  catch (error) {
    console.error(error);
    return { success: false }
  }
};

export async function createTicketComment(ticketId, data) {
  try {
    const createTicketCommentURL = new URL(`/v1/tickets/${ticketId}/comments`, API);
    const commentJSON = {
      content: data.content,
      ref: data.referenceId,
    };

    const response = await fetch(createTicketCommentURL.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: authorizationHeader(),
      },
      body: JSON.stringify(commentJSON),
    });

    if (!response.ok) return failure;

    const responseJSON = await response.json();

    return {
      success: true,
      ...responseJSON.comment,
    }
  }

  catch (error) {
    console.warn(error);
    return failure;
  }
}

export async function getTicketsCount(filter) {
  try {
    const baseURL = new URL("/v1/tickets/count", API)
    const countTicketsURL = ticketFilter(baseURL, filter);

    const response = await fetch(countTicketsURL.toString(), {
      method: "GET",
      headers: {
        Authorization: authorizationHeader(),
      },
    });

    if (!response.ok) {
      return failure;
    }

    const responseJSON = await response.json();
    const result = {
      success: true,
      count: responseJSON.count,
    }

    return result;
  }

  catch (error) {
    console.error(error);
    return failure;
  }
}

export async function uploadContentBlob(blob, ref) {
  try {
    const cblob = await imageCompression(blob, {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
    });
    const formData = new FormData();
    formData.set("file", cblob);
    formData.set("ref", ref);
    const ucbURL = new URL("/v1/tickets/contents", API);
    const response = await axios.post(ucbURL.toString(), formData, {
      mode: "cors",
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: authorizationHeader(),
      },
    });

    return {
      success: true,
      ...response.data,
    };
  }

  catch (error) {
    console.error(error);
    return failure;
  }
}

export async function fetchUpdatedTicketsCount() {
  return async (dispatch) => {
    const result = await getTicketsCount({ unread: true });
    if (!result.success) return;
    dispatch({
      type: TICKET.UNREAD_COUNT,
      data: result,
    });
  };
};

export const getTicketsV1 = (filters) => {
  const query = new URLSearchParams({
    limit: 500,
  });

  // Conditionally filter by status
  if (!!filters?.status && filters?.status.length) query.append("status", filters.status.join(","));
  else query.append("status", "active,processing,solved");

  // Conditionally filter by type
  if (!!filters?.type && filters?.type.length)
    query.append("type", getTypeFilter(filters.type).join(","));

  // Conditionally filter by creation date
  // NOTE: Back-end will return H-1 from specified date
  if (!!filters?.createdStartDate)
    query.append("created_start_date", moment(filters.createdStartDate).format("YYYY-MM-DD"));
  if (!!filters?.createdEndDate)
    query.append(
      "created_end_date",
      moment(filters.createdEndDate).add(1, "day").format("YYYY-MM-DD")
    );

  if (filters.search) {
    query.append("search", filters.search);
  }

  const filterText = query.toString();

  return fetch(`${API}/v1/tickets?${filterText}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
};


// @deprecated
export const getTicketV1 = (id) => {
  return fetch(`${API}/v1/tickets/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
};

// @deprecated
export const createTicketCommentV1 = (id, data) => {
  return fetch(`${API}/v1/tickets/${id}/comments`, {
    mode: "cors",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
};

// @deprecated
export const getTicketsSummary = (filters) => {
  const query = new URLSearchParams({});

  // Conditionally filter by status
  if (!!filters?.status && filters?.status.length) query.append("status", filters.status.join(","));

  // Conditionally filter by type
  if (!!filters?.type && filters?.type.length)
    query.append("type", getTypeFilter(filters.type).join(","));

  // Conditionally filter by creation date
  // NOTE: Back-end will return H-1 from specified date
  if (!!filters?.createdStartDate)
    query.append("created_start_date", moment(filters.createdStartDate).format("YYYY-MM-DD"));
  if (!!filters?.createdEndDate)
    query.append(
      "created_end_date",
      moment(filters.createdEndDate).add(1, "day").format("YYYY-MM-DD")
    );

  const filterText = query.toString();

  return fetch(`${API}/v1/tickets/summary?${filterText}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
};

// @deprecated
export const uploadContentImage = async (image) => {
  const data = new FormData();
  data.append("support_image", image);
  const res = await axios.post(`${API}/v1/files/ticket`, data, {
    mode: "cors",
    method: "POST",
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${isAuthenticated().token}`,
    },
  });
  return res.data;
};

